import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { API_BUSINESS_URL } from '@/utils';
import type { SelectInputOption } from '@/components/inputs/SelectInput';
import type { PaginatedRequest, PaginatedResponse } from '@/types';

export interface LogisticOperatorApi {
  id: string;
  name: string;
  businessName: string;
  status: string;
  taxNumber?: string;
  externalId?: string;
  businessKey?: string;
}

interface LogisticOperatorsResponse {
  data?: {
    items?: LogisticOperatorApi[];
    rows?: LogisticOperatorApi[];
    total?: number;
  };
  items?: LogisticOperatorApi[];
  rows?: LogisticOperatorApi[];
  total?: number;
}

interface GetLogisticOperatorsParams {
  search?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

interface GetLogisticOperatorsPaginatedParams {
  paginated: PaginatedRequest;
  search?: string;
  status?: string;
}

const logisticOperatorsService = createApi({
  reducerPath: 'commerceLogisticOperatorsService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}`),
  tagTypes: ['CommerceLogisticOperators'],
  endpoints: (builder) => ({
    getLogisticOperators: builder.query<SelectInputOption[], void>({
      query: () => ({
        url: '/logistic-operator',
        params: {
          status: 'ACTIVE',
          limit: 1000,
          offset: 0,
        },
      }),
      transformResponse: (response: any): SelectInputOption[] => {
        const data = response?.data ?? response ?? {};
        const rows = Array.isArray(data.items)
          ? data.items
          : Array.isArray(data.rows)
            ? data.rows
            : Array.isArray(data)
              ? data
              : [];
        return rows
          .map((operator: any) => ({
            value: String(operator.id ?? ''),
            label: `${operator.name} - ${operator.businessName}`,
          }))
          .filter((o: SelectInputOption) => o.value && o.label);
      },
      providesTags: ['CommerceLogisticOperators'],
    }),
    getLogisticOperatorsPaginated: builder.query<
      PaginatedResponse<LogisticOperatorApi>,
      GetLogisticOperatorsPaginatedParams
    >({
      query: ({ paginated, search }) => ({
        url: '/logistic-operator',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(search && search.trim() !== '' && { name: search.trim() }),
        },
      }),
      transformResponse: (
        response: LogisticOperatorsResponse
      ): PaginatedResponse<LogisticOperatorApi> => {
        const data = response?.data ?? response ?? {};
        const rows = Array.isArray(data.items)
          ? data.items
          : Array.isArray(data.rows)
            ? data.rows
            : Array.isArray(response)
              ? response
              : [];

        return {
          items: rows,
          count: data.total ?? rows.length,
        };
      },
      providesTags: ['CommerceLogisticOperators'],
    }),
    
    getUnscopedLogisticOperatorsPaginated: builder.query<
      PaginatedResponse<LogisticOperatorApi>,
      GetLogisticOperatorsPaginatedParams
    >({
      query: ({ paginated, search, status }) => ({
        url: '/logistic-operator/unscoped',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(search && search.trim() !== '' && { name: search.trim() }),
          ...(status && status.trim() !== '' && { status: status.trim() }),
        },
      }),
      transformResponse: (
        response: LogisticOperatorsResponse
      ): PaginatedResponse<LogisticOperatorApi> => {
        const data = response?.data ?? response ?? {};
        const rows = Array.isArray(data.items)
          ? data.items
          : Array.isArray(data.rows)
            ? data.rows
            : Array.isArray(response)
              ? response
              : [];

        return {
          items: rows,
          count: data.total ?? rows.length,
        };
      },
      providesTags: ['CommerceLogisticOperators'],
    }),
    searchLogisticOperators: builder.query<
      LogisticOperatorApi[],
      GetLogisticOperatorsParams
    >({
      query: (params) => ({
        url: '/logistic-operator',
        params: {
          limit: params.limit || 9999,
          offset: params.offset || 0,
          ...(params.search &&
            params.search.trim() !== '' && { name: params.search.trim() }),
        },
      }),
      transformResponse: (
        response: LogisticOperatorsResponse
      ): LogisticOperatorApi[] => {
        const data = response?.data ?? response ?? {};
        const rows = Array.isArray(data.items)
          ? data.items
          : Array.isArray(data.rows)
            ? data.rows
            : Array.isArray(response)
              ? response
              : [];

        return rows;
      },
      providesTags: ['CommerceLogisticOperators'],
    }),
  }),
});

export default logisticOperatorsService;
export const {
  useGetLogisticOperatorsQuery,
  useGetLogisticOperatorsPaginatedQuery,
  useGetUnscopedLogisticOperatorsPaginatedQuery,
  useSearchLogisticOperatorsQuery,
} = logisticOperatorsService;
